import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  assessmentData,
  computeScores,
  TARGET_INSTITUTIONS,
} from "../data/assessmentData";
import IndicatorRow from "../components/IndicatorRow";
import ScoreSummary from "../components/ScoreSummary";
import { assessmentAPI } from "../utils/api";
import { useAuth } from "../utils/AuthContext";
import { useToast } from "../utils/ToastContext";
import "./Assessment.css";

const EMPTY_META = {
  institutionName: "",
  generationTier: "Gen 1",
  assessmentDate: "",
  leadAssessor: "",
};

const EMPTY_NARRATIVES = { p1: {}, p2: {}, p3: {} };

function normaliseEvidence(raw) {
  if (!raw) return {};

  if (Array.isArray(raw)) {
    return raw.reduce((acc, item) => {
      if (item?.indicator && item.text !== undefined && item.text !== null) {
        acc[item.indicator] = item.text;
      }
      return acc;
    }, {});
  }

  if (raw instanceof Map) {
    return Object.fromEntries(
      Array.from(raw.entries()).filter(
        ([indicator, text]) => indicator && text !== undefined && text !== null,
      ),
    );
  }

  return Object.fromEntries(
    Object.entries(raw).filter(
      ([indicator, text]) => indicator && text !== undefined && text !== null,
    ),
  );
}

function evidenceFromScores(scores) {
  if (!Array.isArray(scores)) return {};

  return scores.reduce((acc, item) => {
    if (
      item?.indicator &&
      item.evidence !== undefined &&
      item.evidence !== null
    ) {
      acc[item.indicator] = String(item.evidence).trim();
    }
    return acc;
  }, {});
}

function normaliseScores(raw) {
  if (!raw) return {};

  if (Array.isArray(raw)) {
    return raw.reduce((acc, item) => {
      if (item?.indicator && item.score !== undefined && item.score !== null) {
        acc[item.indicator] = Number(item.score);
      }
      return acc;
    }, {});
  }

  if (raw instanceof Map) {
    return Object.fromEntries(
      Array.from(raw.entries())
        .filter(
          ([indicator, score]) =>
            indicator && score !== undefined && score !== null,
        )
        .map(([indicator, score]) => [indicator, Number(score)]),
    );
  }

  return Object.fromEntries(
    Object.entries(raw)
      .filter(
        ([indicator, score]) =>
          indicator && score !== undefined && score !== null,
      )
      .map(([indicator, score]) => [indicator, Number(score)]),
  );
}

function getFirstScoredTab(scoresMap) {
  for (const pillar of assessmentData.pillars) {
    const hasScore = pillar.domains.some((domain) =>
      domain.indicators.some((indicator) => scoresMap[indicator.code] >= 1),
    );
    if (hasScore) {
      return pillar.id;
    }
  }

  return "meta";
}

export default function AssessmentForm() {
  const navigate = useNavigate();
  const params = useParams();
  const routeId = params.id && params.id !== "new" ? params.id : null;

  const toast = useToast();
  const { isAdmin } = useAuth();

  const isEdit = !!routeId;

  const [meta, setMeta] = useState(EMPTY_META);
  const [scores, setScores] = useState({});
  const [narratives, setNarratives] = useState(EMPTY_NARRATIVES);
  const [evidence, setEvidence] = useState({});
  const [assessmentId, setAssessmentId] = useState(routeId);
  const [attachments, setAttachments] = useState({});
  const scoresRef = useRef(scores);
  const evidenceRef = useRef(evidence);

  // ✅ FIX 1: default tab is META (NOT pillar 1)
  const [activeTab, setActiveTab] = useState("meta");

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [showConfirm, setShowConfirm] = useState(false);
  const formRef = useRef(null);

  // Load existing assessment for editing
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    assessmentAPI
      .getById(routeId)
      .then(({ data }) => {
        const a = data.data;
        console.log("EDIT ASSESSMENT:", a);
        console.log(
          "INDICATORS WITH ATTACHMENTS:",
          a.indicators?.map((item) => ({
            indicator: item.indicator,
            attachments: item.evidence?.attachments,
          })),
        );
        const restoredScores = Object.fromEntries(
          (a.indicators || []).map((item) => [item.indicator, item.score]),
        );

        const restoredEvidence = Object.fromEntries(
          (a.indicators || []).map((item) => [
            item.indicator,
            item.evidence?.text || "",
          ]),
        );

        const restoredAttachments = Object.fromEntries(
          (a.indicators || []).map((item) => [
            item.indicator,
            item.evidence?.attachments || [],
          ]),
        );
        setMeta({
          institutionName: a.institutionName,
          generationTier: a.generationTier,
          assessmentDate: a.assessmentDate?.split("T")[0] || "",
          leadAssessor: a.leadAssessor,
        });
        setScores(restoredScores);
        setNarratives(a.narratives || EMPTY_NARRATIVES);
        setEvidence(restoredEvidence);
        setAttachments(restoredAttachments);
        setAssessmentId(a._id);

        scoresRef.current = restoredScores;
        evidenceRef.current = restoredEvidence;
        if (restoredScores && Object.keys(restoredScores).length > 0) {
          setActiveTab(getFirstScoredTab(restoredScores));
        } else {
          setActiveTab("p1");
        }
      })
      .catch(() => toast("Failed to load assessment", "error"))
      .finally(() => setLoading(false));
  }, [routeId, isEdit, toast]);

  const handleAttachmentsChange = useCallback((code, files) => {
    setAttachments((current) => ({
      ...current,
      [code]: files,
    }));
  }, []);

  const handleScore = useCallback(
    (code, val) => {
      // Do not allow scoring before the assessment has been initialized.
      if (!assessmentId) {
        toast("Please complete the assessment information first", "error");
        return;
      }

      const current = scoresRef.current || {};

      const next =
        val === null
          ? { ...current, [code]: undefined }
          : { ...current, [code]: val };

      scoresRef.current = next;
      setScores(next);
    },
    [assessmentId, toast],
  );

  const handleNarrative = useCallback((pillar, key, value) => {
    setNarratives((n) => ({
      ...n,
      [pillar]: { ...n[pillar], [key]: value },
    }));
  }, []);

  const handleEvidence = useCallback((code, value) => {
    const current = evidenceRef.current || {};
    const next = {
      ...current,
      [code]: value,
    };
    evidenceRef.current = next;
    setEvidence(next);
  }, []);

  const computed = computeScores(scores);

  const handleSave = async (status = "draft") => {
    if (!meta.institutionName || !meta.assessmentDate || !meta.leadAssessor) {
      toast("Please complete all required meta fields", "error");
      return;
    }

    setSaving(true);
    try {
      const latestScores = scoresRef.current || scores;
      const latestEvidence = evidenceRef.current || evidence;
      const latestAttachments = attachments;

      if (status === "complete") {
        const missingScores = assessmentData.pillars
          .flatMap((pillar) =>
            pillar.domains.flatMap((domain) => domain.indicators),
          )
          .map((indicator) => indicator.code)
          .filter((code) => latestScores[code] === undefined);

        if (missingScores.length) {
          toast(
            `Please select a score for all indicators. Missing: ${missingScores.join(", ")}`,
            "error",
          );
          setSaving(false);
          return;
        }

        const missingEvidence = assessmentData.pillars
          .flatMap((pillar) =>
            pillar.domains.flatMap((domain) => domain.indicators),
          )
          .map((indicator) => indicator.code)
          .filter((code) => {
            const hasText =
              typeof latestEvidence[code] === "string" &&
              latestEvidence[code].trim().length > 0;

            const hasAttachment =
              Array.isArray(attachments[code]) && attachments[code].length > 0;

            return !hasText && !hasAttachment;
          });

        if (missingEvidence.length) {
          toast(
            `Please provide evidence for all scored indicators. Missing: ${missingEvidence.join(", ")}`,
            "error",
          );
          setSaving(false);
          return;
        }
      }

      const formattedIndicators = Object.entries(latestScores)
        .filter(([_, value]) => value !== undefined)
        .map(([indicator, score]) => ({
          indicator,
          score,
          evidence: {
            text: latestEvidence[indicator] || "",
            attachments: (attachments[indicator] || []).map(
              (file) => file.id || file._id,
            ),
          },
        }));

      const payload = {
        ...meta,
        assessmentId: assessmentId || routeId,
        indicators: formattedIndicators,
        narratives,
        status,
      };

      if (assessmentId || isEdit) {
        const targetId = assessmentId || routeId;
        const { data } = await assessmentAPI.update(targetId, payload);
        setAssessmentId(data.data._id);
      } else {
        const { data } = await assessmentAPI.create(payload);
        const createdId = data.data._id;
        setAssessmentId(createdId);
        navigate(`/assessment/${createdId}/edit`, { replace: true });
      }

      toast(
        status === "complete"
          ? "Assessment saved as complete!"
          : "Draft saved successfully",
        "success",
      );

      if (status === "complete") navigate("/history");
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (err.code === "ERR_NETWORK"
          ? "Cannot reach server — is the backend running on port 5000?"
          : null) ||
        err.message ||
        "Save failed";

      toast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const pillarOrder = ["p1", "p2", "p3"];
  const activePillar = assessmentData.pillars.find((p) => p.id === activeTab);

  // ✅ FIX 2: scroll to top helper
  const goToPillar = (nextId) => {
    setActiveTab(nextId);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading)
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "4rem" }}
      >
        <div
          className="spinner"
          style={{ width: 40, height: 40, borderWidth: 3 }}
        />
      </div>
    );

  return (
    <div>
      {/* Page header */}
      <div className="section-heading">
        <div>
          <h1 className="section-title">
            {isEdit ? "Edit Assessment" : "New Assessment"}
          </h1>
          <p className="section-sub">
            AHC PHC Capacity Assessment V5 — HSDIP-I Aligned
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
      </div>

      <div className="assessment-layout">
        {/* Sidebar */}
        <aside className="sidebar">
          <div className="sidebar-title">Assessment Navigation</div>

          <div
            onClick={() => setActiveTab("meta")}
            className={`sidebar-nav-item${activeTab === "meta" ? " active" : ""}`}
            style={{ cursor: "pointer" }}
          >
            <span>📋 Meta / Info</span>
            <span className="sidebar-nav-badge">
              {Object.values(meta).filter(Boolean).length}/4
            </span>
          </div>

          {assessmentData.pillars.map((p) => {
            const pillarScored = p.domains
              .flatMap((d) => d.indicators)
              .filter((i) => scores[i.code] >= 1).length;

            const pillarTotal = p.domains.flatMap((d) => d.indicators).length;

            return (
              <div
                key={p.id}
                onClick={() => setActiveTab(p.id)}
                className={`sidebar-nav-item ${p.id}${activeTab === p.id ? " active" : ""}`}
                style={{ cursor: "pointer" }}
              >
                <span>
                  {p.emoji} Pillar {p.code.replace("P", "")}
                </span>
                <span className="sidebar-nav-badge">
                  {pillarScored}/{pillarTotal}
                </span>
              </div>
            );
          })}

          <div
            style={{
              marginTop: "1rem",
              borderTop: "1px solid var(--border)",
              paddingTop: "1rem",
            }}
          >
            <ScoreSummary
              computed={computed}
              scores={scores}
              canViewResults={isAdmin}
            />
          </div>
        </aside>

        {/* Main form */}
        <div className="form-section">
          {/* META */}
          {activeTab === "meta" && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">📋 Assessment Information</span>
              </div>

              <div className="card-body">
                <div className="meta-form-grid">
                  <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                    <label className="form-label">Institution Name *</label>
                    <input
                      className="form-input"
                      value={meta.institutionName}
                      onChange={(e) =>
                        setMeta({ ...meta, institutionName: e.target.value })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Generation Tier *</label>
                    <select
                      className="form-select"
                      value={meta.generationTier}
                      onChange={(e) =>
                        setMeta({ ...meta, generationTier: e.target.value })
                      }
                    >
                      {["Gen 1", "Gen 2", "Gen 3", "Gen 4", "Other"].map(
                        (g) => (
                          <option key={g}>{g}</option>
                        ),
                      )}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Assessment Date *</label>
                    <input
                      type="date"
                      className="form-input"
                      value={meta.assessmentDate}
                      onChange={(e) =>
                        setMeta({ ...meta, assessmentDate: e.target.value })
                      }
                    />
                  </div>

                  <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                    <label className="form-label">Lead Assessor *</label>
                    <input
                      className="form-input"
                      value={meta.leadAssessor}
                      onChange={(e) =>
                        setMeta({ ...meta, leadAssessor: e.target.value })
                      }
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={saving}
                  onClick={async () => {
                    if (
                      !meta.institutionName ||
                      !meta.assessmentDate ||
                      !meta.leadAssessor
                    ) {
                      toast(
                        "Please complete all required assessment information first",
                        "error",
                      );
                      return;
                    }

                    setSaving(true);

                    try {
                      // Create the draft assessment now.
                      const { data } = await assessmentAPI.create({
                        ...meta,
                        indicators: [],
                        narratives,
                        status: "draft",
                      });

                      const createdId = data.data._id;

                      setAssessmentId(createdId);

                      // Immediately move to Pillar 1.
                      setActiveTab("p1");

                      // Update URL without reloading the page.
                      navigate(`/assessment/${createdId}/edit`, {
                        replace: true,
                      });

                      window.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      });
                    } catch (err) {
                      toast(
                        err.response?.data?.message ||
                        err.message ||
                        "Failed to start assessment",
                        "error",
                      );
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  {saving ? <span className="spinner" /> : "Begin Assessment →"}
                </button>
              </div>
            </div>
          )}

          {/* PILLARS */}
          {assessmentData.pillars.map(
            (pillar) =>
              activeTab === pillar.id && (
                <div key={pillar.id}>
                  {/* ✅ FIX 3: show pillar NAME at top */}
                  <div
                    className="card"
                    style={{
                      marginBottom: "1rem",
                      borderLeft: `4px solid ${pillar.color}`,
                    }}
                  >
                    <div className="card-body">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <div>
                          <div style={{ fontSize: "1.2rem", fontWeight: 800 }}>
                            {pillar.emoji} {pillar.name}
                          </div>
                          <div
                            style={{
                              fontSize: "0.8rem",
                              color: "var(--text-3)",
                            }}
                          >
                            Pillar {pillar.code.replace("P", "")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Domains */}
                  {pillar.domains.map((domain) => (
                    <div key={domain.id} className="domain-section">
                      <div className="domain-header">
                        <span>{domain.name}</span>
                      </div>

                      {domain.indicators.map((ind) => (
                        <IndicatorRow
                          key={ind.code}
                          indicator={ind}
                          score={scores[ind.code] ?? null}
                          evidence={evidence[ind.code] ?? ""}
                          attachments={attachments[ind.code] ?? []}
                          onChange={handleScore}
                          onEvidenceChange={handleEvidence}
                          onAttachmentsChange={handleAttachmentsChange}
                          assessmentId={assessmentId}
                        />
                      ))}
                    </div>
                  ))}

                  {/* Navigation */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "1rem",
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => {
                        const i = pillarOrder.indexOf(pillar.id);
                        const prev = i === 0 ? "meta" : pillarOrder[i - 1];
                        setActiveTab(prev);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    >
                      ← Previous
                    </button>

                    {pillarOrder.indexOf(pillar.id) < pillarOrder.length - 1 ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          const next =
                            pillarOrder[pillarOrder.indexOf(pillar.id) + 1];
                          goToPillar(next);
                        }}
                      >
                        Next Pillar →
                      </button>
                    ) : (
                      <div style={{ display: "flex", gap: "0.75rem" }}>
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={() => handleSave("draft")}
                          disabled={saving}
                        >
                          {saving ? (
                            <span className="spinner" />
                          ) : (
                            "💾 Save Draft"
                          )}
                        </button>

                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={() => handleSave("complete")}
                          disabled={saving}
                        >
                          {saving ? (
                            <span className="spinner" />
                          ) : (
                            "✓ Complete Assessment"
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ),
          )}
        </div>
      </div>
    </div>
  );
}
